import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { track } from "@/lib/track";
import { attachAnonDiag, getAnonDiag } from "@/lib/anonDiag";
import { Button, Card } from "@/components/ui-kit";
import { Sparkles } from "lucide-react";
import { APP } from "@/config";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const fromDiagnostic = params.get("from") === "diagnostic" || !!getAnonDiag();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "parent", grade: 6, childName: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        // A logged-in parent returning mid-funnel still attaches their diagnostic.
        const sid = await attachAnonDiag(form.childName);
        toast.success("Welcome!");
        nav(sid ? `/student/${sid}/today?from=diagnostic` : "/home");
      } else {
        const payload = { email: form.email, password: form.password, name: form.name, role: form.role };
        if (form.role === "student") payload.grade = Number(form.grade);
        await register(payload);
        if (fromDiagnostic && form.role === "parent") {
          track("diagnostic_signup_completed", {});
          const sid = await attachAnonDiag(form.childName);
          toast.success("Welcome! We've built your child's plan.");
          nav(sid ? `/student/${sid}/today?from=diagnostic` : "/parent");
        } else {
          toast.success("Welcome!");
          nav("/home");
        }
      }
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not sign in");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => setForm({ ...form, email: "parent@numbersense.app", password: "Parent123!" });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)] grid place-items-center px-5 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6 cursor-pointer" onClick={() => nav("/")}>
          <div className="w-9 h-9 rounded-xl bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold">N</div>
          <span className="font-head text-lg font-bold text-[var(--ns-text)]">{APP.name}</span>
        </div>

        <Card className="p-6 sm:p-8 ns-rise">
          {fromDiagnostic && mode === "register" && (
            <div className="mb-5 flex items-start gap-3 rounded-[var(--ns-radius)] p-3" style={{ background: "var(--ns-primary-light)" }} data-testid="diag-attach-banner">
              <Sparkles size={18} className="text-[var(--ns-primary)] mt-0.5 shrink-0" />
              <p className="text-sm text-[var(--ns-text)]">
                Create your free account and we'll build your child's plan from the diagnostic you just completed.
              </p>
            </div>
          )}
          <div className="flex rounded-full bg-[var(--ns-surface-subtle)] p-1 mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`tab-${m}`}
                onClick={() => setMode(m)}
                className="flex-1 rounded-full py-2 text-sm font-semibold transition-colors"
                style={{ background: mode === m ? "var(--ns-surface)" : "transparent", color: mode === m ? "var(--ns-primary)" : "var(--ns-muted)", boxShadow: mode === m ? "0 1px 4px rgba(0,0,0,0.06)" : "none" }}
              >
                {m === "login" ? "Log in" : "Sign up"}
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="google-signin-btn"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] py-3 font-semibold text-[var(--ns-text)] transition-colors hover:border-[var(--ns-primary)]"
            style={{ minHeight: "var(--ns-touch)" }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
              <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
            </svg>
            Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-[var(--ns-border)]" />
            <span className="text-xs uppercase tracking-wider text-[var(--ns-subtle)] font-semibold">or</span>
            <div className="h-px flex-1 bg-[var(--ns-border)]" />
          </div>

          <form onSubmit={submit} className="grid gap-4">
            {mode === "register" && (
              <>
                <Field label="Your name" testid="name" value={form.name} onChange={set("name")} required />
                {fromDiagnostic && (
                  <Field label="Your child's first name" testid="child-name" value={form.childName} onChange={set("childName")} placeholder="e.g. Maya" />
                )}
                {!fromDiagnostic && (
                <div>
                  <label className="text-sm font-semibold text-[var(--ns-text)]">I am a…</label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {[["parent", "Parent"], ["student", "Student (13+)"]].map(([v, l]) => (
                      <button type="button" key={v} data-testid={`role-${v}`} onClick={() => setForm({ ...form, role: v })}
                        className="rounded-[var(--ns-radius)] border-2 py-2 text-sm font-semibold"
                        style={{ borderColor: form.role === v ? "var(--ns-primary)" : "var(--ns-border)", background: form.role === v ? "var(--ns-primary-light)" : "transparent", color: form.role === v ? "var(--ns-primary-hover)" : "var(--ns-muted)" }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                )}
                {form.role === "student" && (
                  <div>
                    <label className="text-sm font-semibold text-[var(--ns-text)]">Grade</label>
                    <select data-testid="grade-select" value={form.grade} onChange={set("grade")}
                      className="mt-2 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-3 py-3">
                      {[3, 4, 5, 6, 7, 8].map((g) => <option key={g} value={g}>Grade {g}</option>)}
                    </select>
                  </div>
                )}
              </>
            )}
            <Field label="Email" testid="email" type="email" value={form.email} onChange={set("email")} required />
            <Field label="Password" testid="password" type="password" value={form.password} onChange={set("password")} required />

            <Button type="submit" data-testid="submit-auth" disabled={loading} className="mt-1">
              {loading ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
            </Button>
          </form>

          {mode === "login" && (
            <button data-testid="fill-demo" onClick={fillDemo} className="mt-4 w-full text-sm text-[var(--ns-primary)] font-semibold hover:underline">
              Use demo parent account
            </button>
          )}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[var(--ns-text)]">{label}</label>
      <input
        data-testid={`input-${testid}`}
        {...props}
        className="mt-2 w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-3 focus:outline-none focus:border-[var(--ns-primary)]"
        style={{ minHeight: "var(--ns-touch)" }}
      />
    </div>
  );
}
